import { useCallback, useMemo, useState } from 'react';
import { EXERCISE_ORDER_RANDOM } from '@cavaquinho/shared';
import { api } from '../../lib/api/client.js';
import type { AdminLesson } from '../../lib/api/types.js';
import { Badge } from '../../ui/primitives/Badge.tsx';
import { Button } from '../../ui/primitives/Button.tsx';
import { IconButton } from '../../ui/primitives/IconButton.tsx';
import { Input } from '../../ui/primitives/Input.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import { DataTable, type DataTableColumn } from '../../ui/components/DataTable/DataTable.tsx';
import { LoadingState } from '../../ui/components/LoadingState/LoadingState.tsx';
import { useAsyncData } from '../../ui/components/LoadingState/useAsyncData.ts';
import { StatRow, StatTile } from '../../ui/components/StatTile/StatTile.tsx';
import { Toolbar } from '../../ui/components/Toolbar/Toolbar.tsx';
import { LessonEditor } from './LessonEditor.tsx';
import { labelForType } from './ExerciseConfigForm.tsx';
import { describeLessonDeletion } from './validation.js';
import { ConfirmDelete } from './ConfirmDelete.tsx';
import styles from './admin.module.css';

/**
 * The administrator's Lesson list, with reordering (T098; FR-001, FR-042).
 *
 * REORDERING SUBMITS THE FULL ORDERED ID LIST, never "move this one to
 * position 3". The server derives every `order` from the array index and
 * rejects a list that is not exactly the current set — so a stale tab whose
 * list is missing a Lesson someone else added gets a 422 rather than silently
 * dropping it. `order` never appears in this file, in either direction; array
 * order is the whole ordering contract (data-model.md § Content entities).
 *
 * The reorder affordance is a pair of move buttons rather than a drag-and-drop
 * surface. Drag-and-drop is what tasks.md names, and it remains a reasonable
 * enhancement — but a pointer-only affordance is unusable by keyboard and by
 * screen reader, and § 12 does not allow a control that some administrators
 * cannot operate. Move-up/move-down expresses the same intent, produces the
 * same full-list payload, and works with a pointer, a keyboard, and assistive
 * technology alike. A drag layer can be added ON TOP of these later without
 * changing the request they send.
 *
 * IT IS A TABLE, NOT A LIST, and it is NOT PAGED — the two follow from the
 * same fact. A table because an administrator compares Lessons across (§ 14);
 * unpaged because the ordered array IS the ordering contract, and a reorder
 * sent from page 2 of a paged list would be a full-list payload the client
 * does not hold. The search below filters the VIEW and disables the move
 * controls while it does, rather than pretending a move inside a filtered
 * list has a meaning.
 */
export function LessonList() {
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AdminLesson | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const loadLessons = useCallback(async () => (await api.adminLessons()).lessons, []);
  const {
    status,
    data: lessons,
    showLoading,
    reload,
    setData: setLessons,
  } = useAsyncData<AdminLesson[]>(loadLessons, []);

  /**
   * Moves one Lesson by one place and sends the WHOLE resulting order.
   *
   * The optimistic local reorder is reverted if the request fails, so the list
   * on screen never claims an order the server did not accept.
   */
  const move = useCallback(
    async (index: number, delta: number) => {
      const target = index + delta;
      if (target < 0 || target >= lessons.length || busy) return;

      const next = [...lessons];
      const [moved] = next.splice(index, 1);
      if (!moved) return;
      next.splice(target, 0, moved);

      const previous = lessons;
      setLessons(next);
      setBusy(true);
      setError(null);
      try {
        const response = await api.reorderLessons(next.map((lesson) => lesson.id));
        // The server's own order is authoritative, so the list is replaced
        // with what it returned rather than left on the optimistic guess.
        setLessons(response.lessons);
      } catch {
        setLessons(previous);
        setError('Não foi possível reordenar. A lista voltou à ordem anterior.');
      } finally {
        setBusy(false);
      }
    },
    [lessons, busy],
  );

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteLesson(pendingDelete.id);
      setPendingDelete(null);
      reload();
    } catch {
      setError('Não foi possível remover a lição.');
    } finally {
      setBusy(false);
    }
  }, [pendingDelete, reload]);

  /*
   * The row carries its position in the FULL list, not in the filtered view:
   * "Lição 3" is the third item in the ordering contract, and a move is
   * always expressed against that same list.
   */
  const rows = useMemo(() => {
    const numbered = lessons.map((lesson, index) => ({ lesson, index }));
    const term = search.trim().toLocaleLowerCase();
    return term
      ? numbered.filter((row) => row.lesson.title.toLocaleLowerCase().includes(term))
      : numbered;
  }, [lessons, search]);

  const filtering = search.trim() !== '';
  const exerciseCount = lessons.reduce((sum, lesson) => sum + lesson.exercises.length, 0);

  /** The Exercise Types actually in use, so the tile counts what is there. */
  const typesInUse = useMemo(() => {
    const seen = new Set<string>();
    for (const lesson of lessons) {
      for (const exercise of lesson.exercises) seen.add(exercise.exerciseType);
    }
    return [...seen];
  }, [lessons]);

  const columns: DataTableColumn<{ lesson: AdminLesson; index: number }>[] = useMemo(
    () => [
      {
        id: 'order',
        header: '#',
        width: 'min',
        align: 'end',
        cell: ({ index }) => <span className="type-numeric-inline">{index + 1}</span>,
      },
      {
        id: 'title',
        header: 'Lição',
        rowHeader: true,
        cell: ({ lesson }) => lesson.title,
      },
      {
        id: 'exercises',
        header: 'Exercícios',
        width: 'min',
        align: 'end',
        cell: ({ lesson }) => <span className="type-numeric-inline">{lesson.exercises.length}</span>,
      },
      {
        id: 'exercise-order',
        header: 'Ordem',
        width: 'min',
        /*
          Which Lessons hand their Exercises out in a draw. It belongs in this
          table rather than only inside each editor: "which of my Lessons are
          sorted?" is a question about the set, and the set is what this screen
          is for.
        */
        cell: ({ lesson }) =>
          lesson.exerciseOrder === EXERCISE_ORDER_RANDOM ? (
            <Badge tone="info">Sorteada</Badge>
          ) : (
            <Text as="span" variant="body-sm" tone="muted">
              Fixa
            </Text>
          ),
      },
      {
        id: 'types',
        header: 'Tipos',
        width: 'min',
        /*
          Chips rather than a comma-joined sentence: this column is scanned
          down, not read across, and three type names joined by commas is a
          paragraph in a table cell. Two are shown and the rest are counted,
          so the row stays one line high however many types a Lesson mixes.
        */
        cell: ({ lesson }) => {
          const types = [...new Set(lesson.exercises.map((one) => one.exerciseType))];
          if (types.length === 0) {
            return (
              <Text as="span" variant="body-sm" tone="muted">
                —
              </Text>
            );
          }
          const shown = types.slice(0, 2);
          const rest = types.length - shown.length;
          return (
            <span className={styles.chips}>
              {shown.map((type) => (
                <Badge key={type} tone="neutral">
                  {labelForType(type)}
                </Badge>
              ))}
              {rest > 0 ? (
                <Text as="span" variant="body-sm" tone="muted">
                  +{rest}
                </Text>
              ) : null}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: 'Ações',
        width: 'min',
        align: 'end',
        cell: ({ lesson, index }) => (
          <div className={styles.rowActions}>
            <span data-testid="move-up">
              <IconButton
                icon="arrow-up"
                size="compact"
                shape="square"
                label={`Mover ${lesson.title} para cima`}
                disabled={index === 0 || busy || filtering}
                disabledReason={
                  filtering
                    ? 'Limpe a busca para reordenar.'
                    : index === 0
                      ? 'Já é a primeira lição.'
                      : 'Salvando a nova ordem.'
                }
                onClick={() => void move(index, -1)}
              />
            </span>
            <span data-testid="move-down">
              <IconButton
                icon="arrow-down"
                size="compact"
                shape="square"
                label={`Mover ${lesson.title} para baixo`}
                disabled={index === lessons.length - 1 || busy || filtering}
                disabledReason={
                  filtering
                    ? 'Limpe a busca para reordenar.'
                    : index === lessons.length - 1
                      ? 'Já é a última lição.'
                      : 'Salvando a nova ordem.'
                }
                onClick={() => void move(index, 1)}
              />
            </span>
            <span data-testid="edit-lesson">
              <Button variant="ghost" size="compact" onClick={() => setEditing(lesson.id)}>
                Editar
              </Button>
            </span>
            <span data-testid="delete-lesson">
              <IconButton
                icon="trash"
                size="compact"
                shape="square"
                label={`Remover ${lesson.title}`}
                onClick={() => setPendingDelete(lesson)}
              />
            </span>
          </div>
        ),
      },
    ],
    [busy, filtering, lessons.length, move],
  );

  if (status === 'loading') return showLoading ? <LoadingState label="Carregando o conteúdo" /> : null;

  if (status === 'unavailable') {
    return (
      <div className={styles.page}>
        <Text as="h1" variant="subheading">
          Não foi possível carregar o conteúdo
        </Text>
        <span>
          <Button onClick={() => reload({ force: true })}>Tentar novamente</Button>
        </span>
      </div>
    );
  }

  if (creating) {
    return (
      <LessonEditor
        onCancel={() => setCreating(false)}
        onSaved={() => {
          setCreating(false);
          reload();
        }}
      />
    );
  }

  if (editing) {
    const lesson = lessons.find((entry) => entry.id === editing);
    if (lesson) {
      return (
        <LessonEditor
          lesson={lesson}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      );
    }
  }

  return (
    <div className={styles.page} data-testid="admin-lesson-list">
      <header className={styles.header}>
        <div className={styles.heading}>
          <Text as="h1" variant="subheading">
            Conteúdo
          </Text>
          <Text as="p" variant="body-sm" tone="muted">
            As lições na ordem em que os alunos as encontram.
          </Text>
        </div>
      </header>

      <StatRow label="Resumo do conteúdo">
        <StatTile label="Lições" value={lessons.length} hint="Na sequência publicada" />
        <StatTile label="Exercícios" value={exerciseCount} hint="Em todas as lições" />
        <StatTile
          label="Tipos em uso"
          value={typesInUse.length}
          hint={typesInUse.map((type) => labelForType(type)).join(', ') || 'Nenhum ainda'}
        />
      </StatRow>

      {/*
        `role="alert"` sits on the wrapper because `src/ui`'s Text owns its own
        DOM and takes no ARIA passthrough. A reorder that failed and silently
        reverted would otherwise be invisible to a screen-reader user — the
        list would simply be back where it started with no explanation.
      */}
      {error ? (
        <div role="alert" data-testid="admin-error">
          <Text as="p" variant="body-sm" tone="error">
            {error}
          </Text>
        </div>
      ) : null}

      <section className={styles.section}>
        <Toolbar
          label="Filtrar lições"
          actions={
            <span data-testid="new-lesson">
              <Button size="compact" icon="plus" onClick={() => setCreating(true)}>
                Nova lição
              </Button>
            </span>
          }
        >
          <Input
            id="lessons-search"
            type="search"
            size="compact"
            icon="search"
            ariaLabel="Buscar lições pelo título"
            placeholder="Buscar pelo título"
            value={search}
            onChange={setSearch}
          />
        </Toolbar>

        <DataTable
          caption="Lições da plataforma"
          testId="lessons"
          rowTestId="lesson-row"
          columns={columns}
          rows={rows}
          rowKey={({ lesson }) => lesson.id}
          busy={busy}
          empty={
            <Text as="p" variant="body-sm" tone="muted">
              {filtering
                ? 'Nenhuma lição corresponde a essa busca.'
                : 'Nenhuma lição ainda. Crie a primeira para que ela apareça na prática.'}
            </Text>
          }
        />

        <Text as="p" variant="body-sm" tone="muted">
          {lessons.length === 1 ? '1 lição' : `${lessons.length} lições`}
          {filtering ? ` · ${rows.length} em exibição` : ''}
        </Text>
      </section>

      {pendingDelete ? (
        <ConfirmDelete
          title={`Remover "${pendingDelete.title}"?`}
          impact={describeLessonDeletion({ exerciseCount: pendingDelete.exercises.length })}
          confirmLabel="Remover lição"
          busy={busy}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => void confirmDelete()}
        />
      ) : null}
    </div>
  );
}
